import type { ChipProps, TooltipProps } from '@mui/material'
import { Chip, Tooltip } from '@mui/material'
import React from 'react'
import type { LinkProps as RouterLinkProps } from 'react-router-dom'
import { Link as RouterLink } from 'react-router-dom'

export interface CommonChipProps extends ChipProps {
  tooltip?: React.ReactNode
  tooltipPlacement?: TooltipProps['placement']
  larger?: boolean | undefined
  /** Renders the chip as a router link, so it can be middle-clicked and opened in a new tab. */
  to?: RouterLinkProps['to']
}

/**
 * CommonChip is a reusable wrapper around MUI's Chip component with consistent styling.
 *
 * It enforces a small size, rounded corners, and label padding for visual consistency across the app.
 * Additionally, it supports an optional tooltip that appears on hover, and a `to` that turns it into a
 * router link.
 */

const CommonChip: React.FC<CommonChipProps> = ({ sx, tooltip, tooltipPlacement = 'bottom', larger, to, ...props }) => {
  const chip = (
    <Chip
      size="small"
      {...props}
      {...(to ? { component: RouterLink, to, clickable: true } : {})}
      sx={{
        borderRadius: 1,
        height: larger ? 35 : 28,
        '& .MuiChip-label': {
          paddingX: larger ? 1.7 : 1.2,
        },
        ...sx,
      }}
    />
  )
  return tooltip ? (
    <Tooltip title={tooltip} placement={tooltipPlacement} arrow>
      {chip}
    </Tooltip>
  ) : (
    chip
  )
}

export default CommonChip
